import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import apiFetch from '../../../utils/apiFetch';
import CompensationDrawer from './CompensationDrawer';
import CompensationItemRow from './CompensationItemRow';
import PayPeriodPreview from './PayPeriodPreview';
import { FONT, API_URL } from '../constants';
import { C } from '../styles';
import { authHeaders } from '../helpers';
import type { Section } from '../types';
import type { DrawerMode, PayFrequency } from './compensationHelpers';

interface Props {
  section: Section;
  isOpen: boolean;
  onToggleOpen: () => void;
  employeeId: string | number | undefined;
}

export default function CompensationSectionCard({ section, isOpen, onToggleOpen, employeeId }: Props) {
  const [earnings, setEarnings] = useState<any[]>([]);
  const [deductions, setDeductions] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | number | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<{ item: any; kind: DrawerMode } | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<{ item: any; kind: DrawerMode } | null>(null);
  const [payFrequency, setPayFrequency] = useState<PayFrequency>('biweekly');

  function refreshData(): void {
    if (!employeeId) { setLoading(false); return; }
    const headers = { 'Content-Type': 'application/json', ...authHeaders() };
    Promise.all([
      apiFetch('/api/v1/employee-pay-items/employee/' + employeeId, { headers }).then((r: Response) => (r.ok ? r.json() : [])),
      apiFetch('/api/v1/employee-deduction-items/employee/' + employeeId, { headers }).then((r: Response) => (r.ok ? r.json() : [])),
    ]).then((results: any[]) => {
      setEarnings(results[0] || []);
      setDeductions(results[1] || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  useEffect(() => { refreshData(); }, [employeeId]);

  const handleRemoveClick = (item: any, kind: DrawerMode): void => {
    setConfirmRemove({ item, kind });
    setOpenMenuId(null);
    setRemoveError(null);
  };

  const handleConfirmRemove = (): void => {
    if (!confirmRemove || removing) return;
    setRemoving(true);
    setRemoveError(null);
    const endpoint = confirmRemove.kind === 'earning'
      ? '/api/v1/employee-pay-items/' + confirmRemove.item.id
      : '/api/v1/employee-deduction-items/' + confirmRemove.item.id;
    const headers = { 'Content-Type': 'application/json', ...authHeaders() };
    fetch(API_URL + endpoint, { method: 'DELETE', headers })
      .then((r) => {
        if (!r.ok && r.status !== 204) {
          return r.text().then((t) => { throw new Error(t || 'Failed to remove'); });
        }
        return null;
      })
      .then(() => {
        setRemoving(false);
        setConfirmRemove(null);
        refreshData();
      })
      .catch((err) => {
        setRemoving(false);
        setRemoveError(err.message || 'Failed to remove. Please try again.');
      });
  };

  const handlePauseToggle = (item: any, kind: DrawerMode): void => {
    const endpoint = kind === 'earning'
      ? '/api/v1/employee-pay-items/' + item.id
      : '/api/v1/employee-deduction-items/' + item.id;
    const headers = { 'Content-Type': 'application/json', ...authHeaders() };
    const newActive = !item.is_active;
    setOpenMenuId(null);
    fetch(API_URL + endpoint, { method: 'PATCH', headers, body: JSON.stringify({ is_active: newActive }) })
      .then((r) => { if (r.ok) refreshData(); });
  };

  const handleEditClick = (item: any, kind: DrawerMode): void => {
    setEditItem({ item, kind });
    setDrawerMode(kind);
    setOpenMenuId(null);
  };

  const summaryText = (earnings.length + deductions.length) === 0
    ? 'Not started'
    : (earnings.length + ' earning' + (earnings.length === 1 ? '' : 's') + ' . ' + deductions.length + ' deduction' + (deductions.length === 1 ? '' : 's'));

  return (
    <div style={{
      background: '#fff', border: '1px solid ' + C.line,
      borderRadius: 15, boxShadow: '0 1px 2px rgba(16,26,43,0.04)',
      overflow: 'hidden',
    }}>
      <div onClick={onToggleOpen} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '18px 22px', cursor: 'pointer' }}>
        <h3 style={{ flex: 1, fontSize: 16, fontWeight: 700, color: C.ink }}>{section.title}</h3>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 11.5, fontWeight: 600,
          padding: '3px 10px', borderRadius: 20,
          background: (earnings.length + deductions.length) > 0 ? C.tealSoft : (C as any).amberSoft,
          color: (earnings.length + deductions.length) > 0 ? C.tealInk : (C as any).amber,
        }}>
          {summaryText}
        </span>
        <ChevronDown size={18} color={(C as any).muted} style={{ transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
      </div>

      {isOpen && (
        <div style={{ padding: '20px 22px', borderTop: '1px solid ' + (C as any).lineSoft }}>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: (C as any).muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Earnings</span>
            <button onClick={() => setDrawerMode('earning')} style={{
              background: '#fff', color: (C as any).text, border: '1px solid ' + C.line,
              borderRadius: 6, padding: '6px 11px', fontWeight: 500, fontSize: 12,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: FONT,
            }}>
              <span style={{ color: C.tealInk, fontSize: 14, lineHeight: 1, fontWeight: 600 }}>+</span>
              Add earning
            </button>
          </div>
          {earnings.length === 0 ? (
            <div style={{ background: '#FCFCFD', border: '1px solid ' + C.line, borderRadius: 8, padding: '32px 20px', textAlign: 'center', marginBottom: 22 }}>
              <div style={{ fontSize: 13, color: (C as any).muted, marginBottom: 4 }}>No earnings assigned yet</div>
              <div style={{ fontSize: 11.5, color: (C as any).faint }}>Click "Add earning" to assign a pay type from your catalog</div>
            </div>
          ) : (
            <div style={{ background: '#FCFCFD', border: '1px solid ' + C.line, borderRadius: 8, overflow: 'visible', marginBottom: 22 }}>
              {earnings.map((item, idx) => (
                <CompensationItemRow
                  key={item.id}
                  item={item}
                  kind="earning"
                  isLast={idx === earnings.length - 1}
                  openMenuId={openMenuId}
                  onToggleMenu={(id) => setOpenMenuId(openMenuId === id ? null : id)}
                  onEdit={() => handleEditClick(item, 'earning')}
                  onPauseToggle={() => handlePauseToggle(item, 'earning')}
                  onRemove={() => handleRemoveClick(item, 'earning')}
                />
              ))}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: (C as any).muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Deductions &amp; contributions</span>
            <button onClick={() => setDrawerMode('deduction')} style={{
              background: '#fff', color: (C as any).text, border: '1px solid ' + C.line,
              borderRadius: 6, padding: '6px 11px', fontWeight: 500, fontSize: 12,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: FONT,
            }}>
              <span style={{ color: C.tealInk, fontSize: 14, lineHeight: 1, fontWeight: 600 }}>+</span>
              Add deduction
            </button>
          </div>
          {deductions.length === 0 ? (
            <div style={{ background: '#FCFCFD', border: '1px solid ' + C.line, borderRadius: 8, padding: '32px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 13, color: (C as any).muted, marginBottom: 4 }}>No deductions assigned yet</div>
              <div style={{ fontSize: 11.5, color: (C as any).faint }}>Click "Add deduction" to assign a deduction type</div>
            </div>
          ) : (
            <div style={{ background: '#FCFCFD', border: '1px solid ' + C.line, borderRadius: 8, overflow: 'visible' }}>
              {deductions.map((item, idx) => (
                <CompensationItemRow
                  key={item.id}
                  item={item}
                  kind="deduction"
                  isLast={idx === deductions.length - 1}
                  openMenuId={openMenuId}
                  onToggleMenu={(id) => setOpenMenuId(openMenuId === id ? null : id)}
                  onEdit={() => handleEditClick(item, 'deduction')}
                  onPauseToggle={() => handlePauseToggle(item, 'deduction')}
                  onRemove={() => handleRemoveClick(item, 'deduction')}
                />
              ))}
            </div>
          )}

          <PayPeriodPreview
            earnings={earnings}
            deductions={deductions}
            payFrequency={payFrequency}
            setPayFrequency={setPayFrequency}
          />

        </div>
      )}

      {confirmRemove && createPortal(
        <div
          onClick={() => { if (!removing) setConfirmRemove(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(14,26,31,0.35)',
            zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, maxWidth: 440, width: '100%',
              boxShadow: '0 20px 40px rgba(14,26,31,0.15)', overflow: 'hidden', fontFamily: FONT,
            }}
          >
            <div style={{ padding: '22px 24px 18px' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.ink, letterSpacing: '-0.005em', marginBottom: 8 }}>
                Remove {confirmRemove.kind === 'earning'
                  ? (confirmRemove.item.pay_type && confirmRemove.item.pay_type.name)
                  : (confirmRemove.item.deduction_type && confirmRemove.item.deduction_type.name)}?
              </div>
              <div style={{ fontSize: 13, color: (C as any).muted, lineHeight: 1.55 }}>
                This will remove this {confirmRemove.kind === 'earning' ? 'pay type' : 'deduction'} from the employee. Your catalog {confirmRemove.kind === 'earning' ? 'pay type' : 'deduction type'} stays intact and can be reassigned later.
              </div>
              {removeError && (
                <div style={{ marginTop: 14, padding: '10px 12px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, color: (C as any).err, fontSize: 12.5, lineHeight: 1.5 }}>
                  {removeError}
                </div>
              )}
            </div>
            <div style={{ padding: '14px 24px 18px', display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid ' + C.line, background: '#FBFCFD' }}>
              <button
                onClick={() => { if (!removing) setConfirmRemove(null); }}
                disabled={removing}
                style={{
                  padding: '9px 16px', borderRadius: 8,
                  fontFamily: FONT, fontWeight: 600, fontSize: 13.5,
                  cursor: removing ? 'not-allowed' : 'pointer',
                  border: '1px solid ' + C.line, color: C.ink, background: '#fff',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRemove}
                disabled={removing}
                style={{
                  padding: '9px 16px', borderRadius: 8,
                  fontFamily: FONT, fontWeight: 600, fontSize: 13.5,
                  cursor: removing ? 'not-allowed' : 'pointer',
                  border: '1px solid transparent', color: '#fff',
                  background: removing ? '#C3CBD6' : (C as any).err,
                  boxShadow: removing ? 'none' : '0 1px 2px rgba(181,59,46,0.25)',
                }}
              >
                {removing ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {drawerMode && (
        <CompensationDrawer
          mode={drawerMode}
          employeeId={employeeId}
          editItem={editItem}
          onClose={() => { setDrawerMode(null); setEditItem(null); }}
          onSaved={() => { setDrawerMode(null); setEditItem(null); refreshData(); }}
        />
      )}
    </div>
  );
}